import { classNames } from '@/utils/classNames';
import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './BottomSheet.module.css';

type BottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  footerClassName?: string;
};

export const BottomSheet = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  footerClassName,
}: BottomSheetProps) => {
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={classNames(styles.overlay, styles.overlayVisible)}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'bottom-sheet-title' : undefined}
    >
      <div className={styles.sheet}>
        <div className={styles.handle} aria-hidden />
        {title && (
          <h2 id="bottom-sheet-title" className={styles.title}>
            {title}
          </h2>
        )}
        <div className={styles.content}>{children}</div>
        {footer && (
          <div className={classNames(styles.footer, footerClassName)}>{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
};
